import { getMap } from './mapInstance.js';
import * as olProj from 'ol/proj.js';
import { Style, Circle as CircleStyle, Fill, Stroke } from 'ol/style.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';
import { getSelectedCountryAbbreviation } from './country-language.js';
import * as MapUtils from './mapUtils.js';

// ---------------------------
// Marker Layer Setup
// ---------------------------
const locationMarkerSource = new VectorSource();
const locationMarkerLayer = new VectorLayer({
  source: locationMarkerSource,
  style: new Style({
    image: new CircleStyle({
      radius: 7,
      fill: new Fill({ color: 'red' }),
      stroke: new Stroke({ color: 'white', width: 2 })
    })
  })
});
locationMarkerLayer.setZIndex(9999);

let map = null;

export function initSearchByLocationLayer() {
  map = getMap();
  if (!map) return;

  if (!map.getLayers().getArray().includes(locationMarkerLayer)) {
    map.addLayer(locationMarkerLayer);
  }
}

// ---------------------------
// Country Bounding Boxes
// [minLon, minLat, maxLon, maxLat]
// ---------------------------
const COUNTRY_BBOX = {
  in: [68.7, 6.5, 97.25, 35.5],   // India
  nl: [3.3, 50.7, 7.2, 53.6],    // Netherlands
  de: [5.8, 47.2, 15.1, 55.1],   // Germany
  be: [2.5, 49.5, 6.4, 51.6]     // Belgium
};

// ---------------------------
// Helper: Hard bbox check
// ---------------------------
function isInsideBbox(lon, lat, bbox) {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  return (
    lon >= minLon &&
    lon <= maxLon &&
    lat >= minLat &&
    lat <= maxLat
  );
}

// ---------------------------
// Geocode with STRICT country filtering
// ---------------------------
async function geocodeAutocomplete(query) {
  const countryCode = getSelectedCountryAbbreviation()?.toLowerCase();
  //console.log('geocodeAutocomplete country:', countryCode);

  const bbox = COUNTRY_BBOX[countryCode];

  let url =
    `https://nominatim.openstreetmap.org/search` +
    `?format=json` +
    `&addressdetails=1` +
    `&limit=20` +
    `&q=${encodeURIComponent(query)}`;

  if (countryCode) {
    url += `&countrycodes=${countryCode}`;
  }

  if (bbox) {
    // bias only - DO NOT bound
    url += `&viewbox=${bbox[0]},${bbox[3]},${bbox[2]},${bbox[1]}`;
  }

  const resp = await fetch(url, {
    headers: {
      'Accept-Language': 'en'
    }
  });

  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

  const data = await resp.json();

  return data.filter(r => {
    if (!r.address || !r.address.country_code) return false;

    if (r.address.country_code.toLowerCase() !== countryCode) return false;

    if (!bbox) return true;

    const lon = parseFloat(r.lon);
    const lat = parseFloat(r.lat);

    // small tolerance for NL borders
    const padding = 0.2;
    return isInsideBbox(lon, lat, [
      bbox[0] - padding,
      bbox[1] - padding,
      bbox[2] + padding,
      bbox[3] + padding
    ]);
  });
}

// ---------------------------
// Search History
// ---------------------------
let searchHistory = [];

function addToHistory(result) {
  if (!searchHistory.find(h => h.display_name === result.display_name)) {
    searchHistory.unshift(result);
    if (searchHistory.length > 5) searchHistory.pop();
  }
}

// ---------------------------
// Show Results Dropdown
// ---------------------------
function showResults(results, resultsContainer) {
  resultsContainer.innerHTML = '';

  if (!results.length && !searchHistory.length) {
    resultsContainer.style.display = 'none';
    return;
  }

  results.forEach(r => {
    const li = document.createElement('li');
    li.className = 'list-group-item list-group-item-action list-group-item-sm';
    li.textContent = r.display_name;
    li.addEventListener('click', () => moveToResult(r, resultsContainer));
    resultsContainer.appendChild(li);
  });

  resultsContainer.style.display = 'block';
}

// ---------------------------
// Move to Result on Map
// ---------------------------
function moveToResult(r, resultsContainer) {
  const lon = parseFloat(r.lon);
  const lat = parseFloat(r.lat);

  const coord =
    map.getView().getProjection().getCode() === 'EPSG:4326'
      ? [lon, lat]
      : olProj.fromLonLat([lon, lat]);

  locationMarkerSource.clear();
  locationMarkerSource.addFeature(new Feature(new Point(coord)));

  map.getView().animate({
    center: coord,
    zoom: 16,
    duration: 600
  });

  MapUtils.setCommand('Search by Location');
  MapUtils.setStatus(`Moved to: ${r.display_name}`);

  addToHistory(r);
  resultsContainer.style.display = 'none';
}

// ---------------------------
// Setup Search Bar
// ---------------------------
export function setupSearchByLocation() {
  const input = document.getElementById('search-location-text');
  const resultsContainer = document.getElementById('search-location-results');

  if (!input || !resultsContainer) return;

  let timeout;

  input.addEventListener('input', () => {
    const val = input.value.trim();

    if (val.length < 3) {
      resultsContainer.style.display = 'none';
      locationMarkerSource.clear();
      return;
    }

    clearTimeout(timeout);
    timeout = setTimeout(async () => {
      try {
        const results = await geocodeAutocomplete(val);
        showResults(results, resultsContainer);
      } catch (err) {
        console.error('Search error:', err);
      }
    }, 300);
  });

  document.addEventListener('click', e => {
    if (!document.getElementById('search-bar').contains(e.target)) {
      resultsContainer.style.display = 'none';
    }
  });

  input.addEventListener('keydown', e => {
    if (e.key === 'Enter' && resultsContainer.children.length > 0) {
      resultsContainer.children[0].click();
    }
  });
}